import { useParams, Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { LineChart } from '@/components/charts/line-chart'
import { Line } from '@/components/charts/line'
import { Grid } from '@/components/charts/grid'
import { XAxis } from '@/components/charts/x-axis'
import { ChartTooltip } from '@/components/charts/tooltip/chart-tooltip'
import { BarChart } from '@/components/charts/bar-chart'
import { Bar } from '@/components/charts/bar'
import { BarYAxis } from '@/components/charts/bar-y-axis'
import { BarXAxis } from '@/components/charts/bar-x-axis'
import { useAppStore } from '@/store'
import { fetchTaxRevenues, fetchTaxSeries, fetchCofogExpenditures, fetchCofogSeries, type TaxItem } from '@/data/eurostat'

const TAX_DESCRIPTIONS: Record<string, string> = {
  D211: 'La TVA (taxe sur la valeur ajoutée) est le principal impôt sur la consommation en France. Elle représente environ 50% des recettes de TVA en Europe.',
  D212: "Les droits d'importation et de douane taxent les marchandises entrant sur le territoire français. Avec l'UE, leur part est devenue marginale.",
  D214: "Cette catégorie regroupe les impôts spécifiques sur les produits : TICPE (carburants), taxes sur les alcools et tabacs (TABAC), et autres droits d'accise.",
  D29: "Les autres impôts sur la production incluent la CFE (cotisation foncière des entreprises), la cotisation minimale, et diverses taxes locales sur les entreprises.",
  D51A: "L'impôt sur le revenu des personnes physiques (IR) est prélevé sur les revenus du travail, du capital et des plus-values. Il est progressif avec 5 tranches.",
  D51B: "L'impôt sur les sociétés (IS) taxe les bénéfices des entreprises. Le taux normal est de 25% depuis 2022.",
  D59: "Les autres impôts courants incluent l'IFI (impôt sur la fortune immobilière), les droits de mutation à titre gratuit (successions/donations), et la taxe foncière.",
  D611: "Les cotisations sociales employeurs sont versées par les entreprises sur les salaires. Elles financent la sécurité sociale, l'assurance chômage et les retraites.",
  D612: "Les cotisations sociales des salariés sont prélevées directement sur le salaire brut (CSG, CRDS, cotisations retraite/maladie).",
  D613: "Les cotisations des non-salariés (indépendants, auto-entrepreneurs) couvrent les mêmes risques mais avec des taux spécifiques.",
}

const COFOG_DESCRIPTIONS: Record<string, string> = {
  GF01: "Les services généraux couvrent le fonctionnement de l'État (pouvoir exécutif, législatif, judiciaire), la diplomatie, et les intérêts de la dette.",
  GF02: 'La défense regroupe les dépenses militaires : personnel armée, équipements, opérations extérieures, et recherche de défense.',
  GF03: "L'ordre et la sécurité publics incluent la police nationale, la gendarmerie, la justice, et le système pénitentiaire.",
  GF04: "Les affaires économiques couvrent l'emploi, l'agriculture, l'industrie, le commerce, le tourisme, et les subventions aux entreprises.",
  GF05: "La protection de l'environnement inclut la gestion des déchets, la qualité de l'air, de l'eau, et la protection de la biodiversité.",
  GF06: "Le logement regroupe les aides au logement (APL), le logement social, et les politiques urbaines.",
  GF07: 'La santé couvre les dépenses hospitalières, les soins ambulatoires, la médecine préventive, et la santé publique.',
  GF08: "Les loisirs, culture et culte incluent le financement de l'éducation artistique, les musées, les bibliothèques, et les monuments historiques.",
  GF09: "L'enseignement couvre l'éducation primaire, secondaire, supérieure, et la formation professionnelle.",
  GF10: "La protection sociale est le premier poste de dépenses : retraites, chômage, maladie, invalidité, famille, et minima sociaux (RSA, AAH).",
}

// Colors for charts (bars, lines, pie slices) — vibrant for visual distinction
const COLORS: Record<string, string> = {
  D211: '#e94560', D212: '#ff6b35', D214: '#f7c948', D29: '#00b894',
  D51A: '#0984e3', D51B: '#6c5ce7', D59: '#e17055',
  D611: '#00cec9', D612: '#fd79a8', D613: '#a29bfe',
  GF01: '#636e72', GF02: '#2d3436', GF03: '#74b9ff', GF04: '#00cec9',
  GF05: '#55efc4', GF06: '#fdcb6e', GF07: '#ff7675', GF08: '#a29bfe',
  GF09: '#0984e3', GF10: '#e17055',
}

// Darkened colors for text display (WCAG AA ≥ 4.5:1 on white)
const TEXT_COLORS: Record<string, string> = {
  D211: '#c73652', D212: '#b24a25', D214: '#8a6c1f', D29: '#008067',
  D51A: '#0768b5', D51B: '#6c5ce7', D59: '#b45944',
  D611: '#007b78', D612: '#b15475', D613: '#716cb1',
  GF01: '#636e72', GF02: '#2d3436', GF03: '#456e99', GF04: '#007b78',
  GF05: '#2a7762', GF06: '#8a6c1f', GF07: '#b25251', GF08: '#716cb1',
  GF09: '#0768b5', GF10: '#b45944',
}

function formatMd(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(0)} Md€`
  return `${value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} M€`
}

export default function DrillDown() {
  const { type, id } = useParams<{ type: string; id: string }>()
  const { selectedYear } = useAppStore()

  if (!type || !id) {
    return <div className="text-center py-12">Paramètres manquants</div>
  }

  const isTax = type === 'tax'

  const currentQuery = useQuery({
    queryKey: [isTax ? 'taxes' : 'cofog', selectedYear],
    queryFn: isTax ? () => fetchTaxRevenues(selectedYear) : () => fetchCofogExpenditures(selectedYear),
    staleTime: 1000 * 60 * 60,
  })

  const seriesQuery = useQuery({
    queryKey: ['series', type, id],
    queryFn: () => isTax
      ? fetchTaxSeries(id, 2010, 2024)
      : fetchCofogSeries(id, 2010, 2024),
    staleTime: 1000 * 60 * 60,
  })

  const items = currentQuery.data ?? []
  const current = items.find((item) => item.code === id)
  const series = seriesQuery.data ?? []
  const description = isTax ? TAX_DESCRIPTIONS[id] : COFOG_DESCRIPTIONS[id]
  const color = COLORS[id] ?? '#94a3b8'
  const textColor = TEXT_COLORS[id] ?? '#94a3b8'

  if (currentQuery.isLoading) {
    return <div className="bg-card rounded-lg shadow p-8 text-center text-text-muted">Chargement…</div>
  }

  if (!current && !isTax) {
    return <div className="bg-card rounded-lg shadow p-8 text-center text-text-muted">Élément introuvable</div>
  }

  const lineData = series.filter((d) => d.value !== null).map((d) => ({ year: d.year, value: d.value }))

  const barData = isTax
    ? items.map((t) => ({ code: t.code, label: ('shortLabel' in t ? (t as TaxItem).shortLabel : t.label) || t.label, value: t.value, pct: t.pctOfTotal }))
    : items.map((e) => ({ code: e.code, label: e.label, value: e.value, pct: e.pctOfTotal }))

  return (
    <div>
      <Link to={isTax ? '/recettes' : '/depenses'} className="text-nav hover:underline text-sm mb-4 inline-block">
        ← Retour
      </Link>

      <div className="flex items-center gap-4 mb-6">
        <div
          className="w-6 h-6 rounded-full"
          style={{ backgroundColor: color }}
        />
        <div>
          <h2 className="text-2xl font-bold">
            {current ? (isTax ? (current as TaxItem).shortLabel : current.label) : id}
          </h2>
          {current && (
            <p className="text-3xl font-bold mt-1" style={{ color: textColor }}>
              {formatMd(current.value)}
              <span className="text-base font-normal text-text-muted ml-2">
                ({current.pctOfTotal.toFixed(1)}% du total)
              </span>
            </p>
          )}
        </div>
      </div>

      {description && (
        <div className="bg-muted rounded-lg p-4 mb-6 text-sm text-text leading-relaxed">
          {description}
        </div>
      )}

      {/* Comparison bar chart */}
      <div className="bg-card rounded-lg shadow p-6 mb-6">
        <h3 className="font-semibold text-lg mb-4">Comparaison avec les autres catégories</h3>
        <BarChart
          data={barData.sort((a, b) => b.value - a.value)}
          xDataKey="label"
          orientation="horizontal"
          aspectRatio="4 / 3"
        >
          <Grid vertical />
          <Bar dataKey="value" fill={color} />
          <BarYAxis />
          <BarXAxis />
          <ChartTooltip />
        </BarChart>
      </div>

      {/* Time series */}
      <div className="bg-card rounded-lg shadow p-6">
        <h3 className="font-semibold text-lg mb-4">Évolution 2010–2024</h3>
        {lineData.length > 0 && (
          <LineChart data={lineData} xDataKey="year" aspectRatio="2 / 1">
            <Grid horizontal />
            <Line dataKey="value" stroke={color} showMarkers />
            <XAxis />
            <ChartTooltip />
          </LineChart>
        )}
      </div>

      <p className="text-xs text-text-muted mt-4">
        Source : Eurostat {isTax ? 'gov_10a_taxag' : 'gov_10a_exp'} — Administrations publiques (S13)
      </p>
    </div>
  )
}
